import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import type { OrgInfo } from '../types';

interface TenantContextType {
  org: OrgInfo | null;
  loading: boolean;
  /** User's role in the current org */
  role: 'owner' | 'admin' | 'member' | null;
  /** Check if user can perform an RBAC-gated action */
  canPerform: (action: string) => boolean;
  /** Refresh org info from the backend */
  refresh: () => Promise<void>;
}

const ROLE_HIERARCHY: Record<string, number> = { owner: 3, admin: 2, member: 1 };

const REQUIRED_ROLE: Record<string, string> = {
  delete_resume: 'admin',
  delete_jd: 'admin',
  bulk_delete_resumes: 'admin',
  view_resume_identity: 'admin',
  view_original_resume: 'admin',
  invite_member: 'admin',
  remove_member: 'admin',
  change_role: 'admin',
  update_org: 'admin',
  delete_org: 'owner',
  manage_migrations: 'admin',
};

const TenantContext = createContext<TenantContextType | null>(null);

export const useTenant = (): TenantContextType => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider');
  return ctx;
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const authFetch = useAuthFetch();
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOrg = useCallback(async () => {
    if (!user) { setOrg(null); setLoading(false); return; }
    try {
      const res = await authFetch('/api/orgs/me');
      if (res.ok) {
        setOrg(await res.json());
      }
    } catch {
      // Org endpoint might not be deployed yet — degrade gracefully
    } finally {
      setLoading(false);
    }
  }, [user, authFetch]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const role = org?.role ?? null;

  const canPerform = useCallback((action: string): boolean => {
    if (!role) return false;
    const required = REQUIRED_ROLE[action] ?? 'member';
    return (ROLE_HIERARCHY[role] ?? 0) >= (ROLE_HIERARCHY[required] ?? 0);
  }, [role]);

  return (
    <TenantContext.Provider value={{ org, loading, role, canPerform, refresh: fetchOrg }}>
      {children}
    </TenantContext.Provider>
  );
};
